import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import {
  type SampleNameTokenDefinitionV1,
  type AnimalIdPrimaryV1,
  type AnimalIdSecondaryV1,
} from '@/model/SampleNameTemplate.model.ts';
import { _isNotBlank, _notNil } from '@/littledash.ts';
import { type FC, useCallback, useState } from 'react';
import { RiCloseLine } from 'react-icons/ri';
import styles from './SampleNameTemplateBuilder.module.scss';
import type { MetadataGlossaryApiId } from '@/model/Metadata.model.ts';

export interface SequenceToken {
  localId: string;
  definition: SampleNameTokenDefinitionV1;
  customValue?: string;
  metadataGlossaryApiId?: MetadataGlossaryApiId;
  metadataGlossaryTitle?: string;
  animalIdPrimary?: AnimalIdPrimaryV1;
  animalIdSecondary?: AnimalIdSecondaryV1;
}

interface TemplateSequenceEditorProps {
  sequence: Array<SequenceToken>;
  onRemove: (localId: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onTokenChange?: (localId: string, patch: Partial<SequenceToken>) => void;
  readOnly?: boolean;
}

const chipLabel = (token: SequenceToken): string => {
  if (typeof token.customValue === 'string') {
    return `"${token.customValue}"`;
  }
  if (token.definition.type === 'separator') {
    return token.definition.value;
  }
  if (token.definition.value === 'sample_meta' && _isNotBlank(token.metadataGlossaryTitle)) {
    return token.metadataGlossaryTitle;
  }
  return token.definition.label;
};

type PriorityItem = 'planned' | 'registered';

const priorityItemLabel = (item: PriorityItem): string => (item === 'planned' ? 'Planned' : 'Name');

const resolveItems = (
  primary: AnimalIdPrimaryV1 | undefined,
  secondary: AnimalIdSecondaryV1 | undefined
): [PriorityItem, PriorityItem | 'none'] => {
  const p: PriorityItem = primary ?? 'planned';
  const s: PriorityItem | 'none' = secondary === undefined ? (p === 'planned' ? 'registered' : 'planned') : secondary;
  return [p, s];
};

interface AnimalIdPriorityPopupProps {
  token: SequenceToken;
  onTokenChange: (localId: string, patch: Pick<SequenceToken, 'animalIdPrimary' | 'animalIdSecondary'>) => void;
  onClose: () => void;
}

/**
 * Popup panel for configuring the Animal ID priority order.
 * Row 1 has a ↓ swap button; row 2 has ↑ swap and × remove buttons.
 * When secondary is removed (none), a + add-fallback row is shown instead.
 */
const AnimalIdPriorityPopup: FC<AnimalIdPriorityPopupProps> = ({ token, onTokenChange, onClose }) => {
  const [first, second] = resolveItems(token.animalIdPrimary, token.animalIdSecondary);
  const hasSecondary = second !== 'none';

  const handleSwap = useCallback(() => {
    if (second === 'none') {
      return;
    }
    onTokenChange(token.localId, { animalIdPrimary: second, animalIdSecondary: first });
  }, [token.localId, first, second, onTokenChange]);

  const handleRemoveSecondary = useCallback(() => {
    onTokenChange(token.localId, { animalIdPrimary: first, animalIdSecondary: 'none' });
  }, [token.localId, first, onTokenChange]);

  const handleAddSecondary = useCallback(() => {
    const defaultSecondary: PriorityItem = first === 'planned' ? 'registered' : 'planned';
    onTokenChange(token.localId, { animalIdSecondary: defaultSecondary });
  }, [token.localId, first, onTokenChange]);

  return (
    <div className={styles.priorityPopup} data-testid="animal-id-priority-popup">
      <div className={styles.priorityPopupHeader}>
        <p className={styles.priorityPopupTitle}>Animal ID priority</p>
        <p className={styles.priorityPopupSubtitle}>
          Choose whether to show registered animal name or planned animal ID.
        </p>
      </div>

      <div className={styles.priorityRow}>
        <span className={styles.priorityRowNumber}>1</span>
        <span className={styles.priorityRowLabel}>{priorityItemLabel(first)}</span>
        <div className={styles.priorityRowActions}>
          {hasSecondary && (
            <button
              type="button"
              className={styles.priorityArrowBtn}
              onClick={handleSwap}
              aria-label={`Move ${priorityItemLabel(first)} down`}
              data-testid="priority-move-down-0"
            >
              ↓
            </button>
          )}
        </div>
      </div>

      {hasSecondary ? (
        <div className={styles.priorityRow}>
          <span className={styles.priorityRowNumber}>2</span>
          <span className={styles.priorityRowLabel}>{priorityItemLabel(second)}</span>
          <div className={styles.priorityRowActions}>
            <button
              type="button"
              className={styles.priorityArrowBtn}
              onClick={handleSwap}
              aria-label={`Move ${priorityItemLabel(second)} up`}
              data-testid="priority-move-up-1"
            >
              ↑
            </button>
            <button
              type="button"
              className={styles.priorityArrowBtn}
              onClick={handleRemoveSecondary}
              aria-label="Remove fallback"
              data-testid="priority-remove-secondary"
            >
              ×
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.priorityAddRow}>
          <button
            type="button"
            className={styles.priorityAddBtn}
            onClick={handleAddSecondary}
            data-testid="priority-add-secondary"
          >
            + Add fallback
          </button>
        </div>
      )}

      <div className={styles.priorityNote}>
        <span>⚠</span>
        <span>
          {hasSecondary ? (
            <>
              Falls back to <strong>{priorityItemLabel(second)}</strong> only if{' '}
              <strong>{priorityItemLabel(first)}</strong> isn't recorded for that sample.
            </>
          ) : (
            <>
              Blank if <strong>{priorityItemLabel(first)}</strong> isn't recorded for that sample.
            </>
          )}
        </span>
      </div>

      <div className={styles.priorityPopupFooter}>
        <button type="button" className="btn btn--primary" onClick={onClose} data-testid="animal-id-priority-done">
          Done
        </button>
      </div>
    </div>
  );
};

/**
 * Displays the ordered token sequence as removable, draggable chips.
 */
export const TemplateSequenceEditor: FC<TemplateSequenceEditorProps> = ({
  sequence,
  onRemove,
  onReorder,
  onTokenChange,
  readOnly = false,
}) => {
  const [openPopupId, setOpenPopupId] = useState<string | null>(null);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination || result.source.index === result.destination.index) {
        return;
      }
      onReorder(result.source.index, result.destination.index);
    },
    [onReorder]
  );

  if (sequence.length === 0) {
    return (
      <div className={styles.sequence}>
        <div className={styles.emptySequence}>Add components to build your template</div>
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className={styles.sequence}>
        <div className={styles.sequenceRow}>
          {sequence.map((token) => {
            const isSeparator = token.definition.type === 'separator';
            const isAnimalId = token.definition.value === 'animal_id' && token.customValue === undefined;
            const badgeLabel = isAnimalId ? priorityItemLabel(token.animalIdPrimary ?? 'planned') : null;
            return (
              <span
                key={token.localId}
                className={`${styles.chip} ${isSeparator ? styles.separatorChip : styles.tokenChip}`}
              >
                {chipLabel(token)}
                {_notNil(badgeLabel) && <span className={styles.chipBadge}>{badgeLabel}</span>}
              </span>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className={styles.sequence}>
        <Droppable droppableId="template-sequence" direction="horizontal">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps} className={styles.sequenceRow}>
              {sequence.map((token, index) => {
                const isSeparator = token.definition.type === 'separator';
                const isAnimalId = token.definition.value === 'animal_id' && token.customValue === undefined;
                return (
                  <Draggable key={token.localId} draggableId={token.localId} index={index}>
                    {(dragProvided, snapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        style={dragProvided.draggableProps.style}
                        className={styles.chipWrapper}
                      >
                        <div
                          className={`${styles.chip} ${isSeparator ? styles.separatorChip : styles.tokenChip} ${snapshot.isDragging ? styles.chipDragging : ''}`}
                        >
                          <span
                            {...dragProvided.dragHandleProps}
                            className={styles.chipDragHandle}
                            aria-label="Drag to reorder"
                          >
                            ⠿
                          </span>
                          <span>{chipLabel(token)}</span>
                          {isAnimalId && _notNil(onTokenChange) && (
                            <button
                              type="button"
                              className={styles.priorityButton}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenPopupId((prev) => (prev === token.localId ? null : token.localId));
                              }}
                              data-testid="animal-id-priority-button"
                            >
                              {token.animalIdPrimary === 'registered' ? 'Name' : 'Planned'}
                            </button>
                          )}
                          <button
                            type="button"
                            className={styles.chipRemove}
                            onClick={() => onRemove(token.localId)}
                            aria-label={`Remove ${chipLabel(token)}`}
                          >
                            <RiCloseLine size={12} />
                          </button>
                        </div>
                        {isAnimalId && openPopupId === token.localId && _notNil(onTokenChange) && (
                          <>
                            <div
                              style={{ position: 'fixed', inset: 0, zIndex: 9499 }}
                              onClick={() => setOpenPopupId(null)}
                            />
                            <AnimalIdPriorityPopup
                              token={token}
                              onTokenChange={onTokenChange}
                              onClose={() => setOpenPopupId(null)}
                            />
                          </>
                        )}
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </DragDropContext>
  );
};
